import { Music2, Youtube, ExternalLink, MapPin, Camera, Film, Palette, Users, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import Newsletter from "../components/Newsletter";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

const AboutPage = () => {
  return (
    <div className="pt-16 min-h-screen" data-testid="about-page">
      <SEO 
        title="About"
        description="Mancunia Rebél empowers creatives across music, fashion, film, photography, and the arts. Based in Manchester, UK since 2014."
        url="/about"
        keywords={["about", "Manchester", "creative magazine", "empowering creatives"]}
      />
      {/* Hero */}
      <section className="py-24 md:py-32 border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <span className="font-mono text-xs uppercase tracking-widest text-primary">Est. 2014</span>
          <h1 className="font-heading font-black text-5xl md:text-8xl uppercase tracking-tighter mt-2 leading-none">
            Empower<br />Creatives.
          </h1>
          <p className="text-muted-foreground text-xl mt-6 max-w-2xl">
            Celebrating media, independent music, fashion, the arts, film, photography, philanthropy, technology, models & independent creatives.
          </p>
        </div>
      </section>

      {/* What We Cover */}
      <section className="py-24 md:py-32 border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <span className="font-mono text-xs uppercase tracking-widest text-primary">What We Cover</span>
              <h2 className="font-heading font-bold text-3xl uppercase tracking-tight mt-2">
                All Creative Disciplines
              </h2>
            </div>
            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border border-border p-6 hover:border-primary/50 transition-colors">
                  <Music2 className="h-6 w-6 text-primary mb-4" />
                  <h3 className="font-heading font-bold uppercase tracking-tight mb-2">Independent Music</h3>
                  <p className="text-muted-foreground text-sm">All genres - hip-hop, electronic, ska, soul, rock, and everything in between. From underground legends to emerging artists.</p>
                </div>
                <div className="border border-border p-6 hover:border-primary/50 transition-colors">
                  <Palette className="h-6 w-6 text-primary mb-4" />
                  <h3 className="font-heading font-bold uppercase tracking-tight mb-2">Fashion & Models</h3>
                  <p className="text-muted-foreground text-sm">Pin-up, fashion, glamour, streetwear. Featuring independent designers and models building careers on their own terms.</p>
                </div>
                <div className="border border-border p-6 hover:border-primary/50 transition-colors">
                  <Film className="h-6 w-6 text-primary mb-4" />
                  <h3 className="font-heading font-bold uppercase tracking-tight mb-2">Film & Video</h3>
                  <p className="text-muted-foreground text-sm">Music videos, documentaries, visual essays, and cinematic storytelling. We create and feature moving image work.</p>
                </div>
                <div className="border border-border p-6 hover:border-primary/50 transition-colors">
                  <Camera className="h-6 w-6 text-primary mb-4" />
                  <h3 className="font-heading font-bold uppercase tracking-tight mb-2">Photography & Arts</h3>
                  <p className="text-muted-foreground text-sm">Visual arts, photography, and creative expression. We also offer professional photography & videography services.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 md:py-32 bg-card border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <span className="font-mono text-xs uppercase tracking-widest text-primary">Our Story</span>
              <h2 className="font-heading font-bold text-3xl uppercase tracking-tight mt-2">
                The Rebél Journey
              </h2>
            </div>
            <div className="lg:col-span-8">
              <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p>
                  Mancunia Rebél Magazine started with Preachaman TV - a video-focused channel documenting Hip-Hop culture in Manchester, UK. Over the years, we've featured legendary artists including KRS One, Inspectah Deck of Wu Tang Clan, Jedi Mind Tricks, Immortal Technique, Vinnie Paz, ILL Bill, Snowgoons, and many more.
                </p>
                <p>
                  But our vision has always been bigger than any single genre. We've evolved to celebrate ALL forms of creative expression - music across all genres, fashion, photography, film, the arts, and technology that empowers creators.
                </p>
                <p>
                  Our goal is simple: <strong className="text-foreground">develop a company that empowers creatives</strong>. Whether you're a musician, model, photographer, filmmaker, designer, or any kind of independent creative - this platform is for you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 md:py-32 border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-mono text-xs uppercase tracking-widest text-primary">Services</span>
              <h2 className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tighter mt-2">
                Photography & Videography
              </h2>
              <p className="text-muted-foreground mt-4 max-w-md">
                Professional visual content at reasonable prices. Artist portraits, music videos, fashion shoots, event coverage, and more.
              </p>
              <div className="mt-8 space-y-4">
                <div className="flex items-center gap-3">
                  <Camera className="h-5 w-5 text-primary" />
                  <span className="text-sm">Artist portraits & press shots</span>
                </div>
                <div className="flex items-center gap-3">
                  <Film className="h-5 w-5 text-primary" />
                  <span className="text-sm">Music videos & visual content</span>
                </div>
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-primary" />
                  <span className="text-sm">Model portfolios & fashion shoots</span>
                </div>
              </div>
              <div className="mt-8">
                <a href="mailto:mancuniarebelmag@gmail.com">
                  <Button className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 font-mono uppercase tracking-widest text-xs">
                    <Mail className="mr-2 h-4 w-4" />
                    Get In Touch
                  </Button>
                </a>
              </div>
            </div>
            <div className="aspect-square relative">
              <img 
                src="https://images.unsplash.com/photo-1516035069371-29a1b244cc32?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Photography services"
                className="w-full h-full object-cover img-grayscale"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Platforms */}
      <section className="py-24 md:py-32 border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-mono text-xs uppercase tracking-widest text-primary">Find Us</span>
              <h2 className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tighter mt-2">
                Across Platforms
              </h2>
              <p className="text-muted-foreground mt-4 max-w-md">
                Mancunia Rebél lives across multiple platforms. Watch our content, stream our features, and connect with the creative community.
              </p>
            </div>
            <div className="space-y-4">
              <a
                href="https://www.youtube.com/@PreachamanTV"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-6 border border-border hover:border-primary/50 transition-colors group"
                data-testid="about-youtube"
              >
                <div className="flex items-center gap-4">
                  <Youtube className="h-6 w-6 text-primary" />
                  <div>
                    <h4 className="font-heading font-bold uppercase tracking-tight group-hover:text-primary transition-colors">Preachaman TV</h4>
                    <p className="font-mono text-xs text-muted-foreground">Watch interviews, music videos & more</p>
                  </div>
                </div>
                <ExternalLink className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </a>
              <a
                href="https://facebook.com/MancuniaRebelMag"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-6 border border-border hover:border-primary/50 transition-colors group"
                data-testid="about-facebook"
              >
                <div className="flex items-center gap-4">
                  <div className="h-6 w-6 flex items-center justify-center text-primary font-heading font-bold">f</div>
                  <div>
                    <h4 className="font-heading font-bold uppercase tracking-tight group-hover:text-primary transition-colors">Facebook</h4>
                    <p className="font-mono text-xs text-muted-foreground">Follow for updates & community</p>
                  </div>
                </div>
                <ExternalLink className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </a>
              <a
                href="https://soundcloud.com/mancunia-rebel-magazine"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-6 border border-border hover:border-primary/50 transition-colors group"
                data-testid="about-soundcloud"
              >
                <div className="flex items-center gap-4">
                  <Music2 className="h-6 w-6 text-primary" />
                  <div>
                    <h4 className="font-heading font-bold uppercase tracking-tight group-hover:text-primary transition-colors">SoundCloud</h4>
                    <p className="font-mono text-xs text-muted-foreground">Stream curated music</p>
                  </div>
                </div>
                <ExternalLink className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </a>
              <a
                href="https://mancuniarebel.tumblr.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-6 border border-border hover:border-primary/50 transition-colors group"
                data-testid="about-tumblr"
              >
                <div className="flex items-center gap-4">
                  <div className="h-6 w-6 flex items-center justify-center text-primary font-heading font-bold">t</div>
                  <div>
                    <h4 className="font-heading font-bold uppercase tracking-tight group-hover:text-primary transition-colors">Tumblr Archive</h4>
                    <p className="font-mono text-xs text-muted-foreground">Explore our visual archive</p>
                  </div>
                </div>
                <ExternalLink className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Support */}
      <section className="py-24 md:py-32 bg-card border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 text-center">
          <span className="font-mono text-xs uppercase tracking-widest text-primary">Support The Movement</span>
          <h2 className="font-heading font-black text-3xl md:text-5xl uppercase tracking-tighter mt-2">
            Help Empower Creatives
          </h2>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Support independent media by grabbing merchandise, donating, or simply sharing our content. Every bit helps us continue amplifying creative voices.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <a href="https://teespring.com/mancuniarebel-merchandise" target="_blank" rel="noopener noreferrer">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 font-mono uppercase tracking-widest text-xs">
                Shop Merchandise
              </Button>
            </a>
            <a href="https://linktr.ee/rcjphotos" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="bg-transparent border border-border text-foreground hover:bg-white hover:text-black h-12 px-8 font-mono uppercase tracking-widest text-xs">
                Collaborate
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-24 md:py-32 border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-mono text-xs uppercase tracking-widest text-primary">Contact</span>
              <h2 className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tighter mt-2">
                Get In Touch
              </h2>
              <p className="text-muted-foreground mt-4 max-w-md">
                Want to feature on the magazine? Looking for photography or videography services? Interested in collaboration? Reach out.
              </p>
            </div>
            <div className="space-y-4">
              <a
                href="mailto:mancuniarebelmag@gmail.com"
                className="flex items-center justify-between p-6 border border-border hover:border-primary/50 transition-colors group"
                data-testid="contact-email"
              >
                <div className="flex items-center gap-4">
                  <Mail className="h-6 w-6 text-primary" />
                  <div>
                    <h4 className="font-heading font-bold uppercase tracking-tight group-hover:text-primary transition-colors">Email</h4>
                    <p className="font-mono text-xs text-muted-foreground">mancuniarebelmag@gmail.com</p>
                  </div>
                </div>
              </a>
              <a
                href="https://facebook.com/MancuniaRebelMag"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-6 border border-border hover:border-primary/50 transition-colors group"
                data-testid="contact-facebook"
              >
                <div className="flex items-center gap-4">
                  <div className="h-6 w-6 flex items-center justify-center text-primary font-heading font-bold">f</div>
                  <div>
                    <h4 className="font-heading font-bold uppercase tracking-tight group-hover:text-primary transition-colors">Facebook</h4>
                    <p className="font-mono text-xs text-muted-foreground">Message us on Facebook</p>
                  </div>
                </div>
                <ExternalLink className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-24 md:py-32 border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 text-center">
          <MapPin className="h-8 w-8 text-primary mx-auto mb-4" />
          <h2 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tighter">
            Manchester, UK
          </h2>
          <p className="font-mono text-sm text-muted-foreground uppercase tracking-wider mt-2">
            Empowering Creatives Worldwide
          </p>
        </div>
      </section>

      {/* Newsletter */}
      <Newsletter />
    </div>
  );
};

export default AboutPage;
